import { useState, useEffect } from "react";
import { Play, Star, Clock, X, Calendar, User, Clapperboard, ChevronRight, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "@/services/api";
import { proxyImageUrl } from "@/utils/imageProxy";
import { isFavorite, toggleFavorite } from "@/utils/watchData";
import { BannerHero } from "@/components/BannerHero";

function SeriesCard({ item, onClick }) {
  const [fav, setFav] = useState(() => isFavorite(item.series_id));

  const handleFav = (e) => {
    e.stopPropagation();
    const added = toggleFavorite({
      id: item.series_id,
      name: item.name,
      poster: item.cover,
      type: 'series',
      genre: item.genre,
      rating: item.rating,
      year: (item.release_date || "").slice(0, 4),
    });
    setFav(added);
  };

  return (
    <div
      data-testid={`series-card-${item.series_id}`}
      onClick={() => onClick(item)}
      className="flex-shrink-0 w-[160px] group cursor-pointer"
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-zinc-800/50 mb-2">
        {item.cover ? (
          <img
            src={proxyImageUrl(item.cover)}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Clapperboard className="w-10 h-10 text-zinc-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
        {item.rating_5based > 0 && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded text-[10px] font-medium text-yellow-400">
            <Star className="w-2.5 h-2.5" fill="currentColor" />
            {item.rating_5based}
          </div>
        )}
        <button
          data-testid={`fav-btn-series-${item.series_id}`}
          onClick={handleFav}
          className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
        >
          <Heart className={`w-3.5 h-3.5 ${fav ? 'text-red-500 fill-red-500' : 'text-white'}`} />
        </button>
      </div>
      <p className="text-sm font-medium truncate text-zinc-200 group-hover:text-white transition-colors">{item.name}</p>
      {item.genre && (
        <p className="text-[11px] text-zinc-500 truncate">{item.genre}</p>
      )}
    </div>
  );
}

function EpisodeRow({ episode, seasonNum, onPlay }) {
  const thumb = episode.info?.movie_image;
  return (
    <div
      data-testid={`episode-${seasonNum}-${episode.episode_num}`}
      onClick={() => onPlay(episode)}
      className="flex items-start gap-4 p-3 rounded-lg hover:bg-zinc-800/60 cursor-pointer transition-colors group"
    >
      {/* Thumbnail */}
      <div className="relative w-36 h-20 flex-shrink-0 rounded-md overflow-hidden bg-zinc-800">
        {thumb ? (
          <img src={proxyImageUrl(thumb)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Clapperboard className="w-6 h-6 text-zinc-600" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
          <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-3.5 h-3.5 text-white ml-0.5" fill="white" />
          </div>
        </div>
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-zinc-500 font-medium">{episode.episode_num}</span>
          <h4 className="text-sm font-medium truncate text-zinc-200 group-hover:text-white transition-colors">
            {episode.title || `Bölüm ${episode.episode_num}`}
          </h4>
        </div>
        {episode.info?.plot && (
          <p className="text-xs text-zinc-500 line-clamp-2 leading-relaxed">{episode.info.plot}</p>
        )}
        {episode.info?.duration && (
          <span className="flex items-center gap-1 text-[11px] text-zinc-600 mt-1">
            <Clock className="w-3 h-3" />
            {episode.info.duration}
          </span>
        )}
      </div>
    </div>
  );
}

function SeriesDetailModal({ item, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState("1");
  const navigate = useNavigate();

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    setLoading(true);
    api.getSeriesDetails(item.series_id).then((data) => {
      if (!cancelled) {
        setDetails(data);
        const seasons = data.episodes ? Object.keys(data.episodes) : [];
        if (seasons.length > 0) setSelectedSeason(seasons[0]);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [item]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!item) return null;

  const info = details?.info || {};
  const episodes = details?.episodes || {};
  const seasons = Object.keys(episodes);
  const currentEpisodes = episodes[selectedSeason] || [];
  const backdrop = info.backdrop_path?.[0] || item.backdrop_path?.[0];
  const poster = info.cover || item.cover;

  const playEpisode = (ep) => {
    navigate(`/app/player/series/${item.series_id}`);
  };

  return (
    <div
      data-testid="series-detail-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-3xl my-8 mx-4 bg-zinc-900 rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Backdrop */}
        <div className="relative h-[320px] overflow-hidden">
          {backdrop ? (
            <img src={proxyImageUrl(backdrop)} alt="" className="w-full h-full object-cover" />
          ) : poster ? (
            <img src={proxyImageUrl(poster)} alt="" className="w-full h-full object-cover blur-sm scale-110" />
          ) : (
            <div className="w-full h-full bg-zinc-800" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-transparent" />

          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-zinc-900/70 flex items-center justify-center hover:bg-zinc-800 transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end gap-5">
            {poster && (
              <img
                src={proxyImageUrl(poster)}
                alt={item.name}
                className="w-28 h-auto rounded-lg shadow-lg flex-shrink-0 border border-zinc-700/40"
              />
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold mb-2 leading-tight">{item.name}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
                {item.release_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {item.release_date.slice(0, 4)}
                  </span>
                )}
                {seasons.length > 0 && (
                  <span>{seasons.length} Sezon</span>
                )}
                {(item.rating || info.rating) && (
                  <span className="flex items-center gap-1 text-yellow-400">
                    <Star className="w-3.5 h-3.5" fill="currentColor" />
                    {parseFloat(item.rating || info.rating).toFixed(1)}
                  </span>
                )}
              </div>
              {(item.genre || info.genre) && (
                <p className="text-xs text-zinc-500 mt-1.5">{item.genre || info.genre}</p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {(info.plot || item.plot) && (
                <p className="text-sm text-zinc-400 leading-relaxed">{info.plot || item.plot}</p>
              )}

              {info.director && (
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-zinc-500 flex-shrink-0">Rejissor:</span>
                  <span className="text-zinc-300">{info.director}</span>
                </div>
              )}

              {(info.cast || item.cast) && (
                <div className="flex items-start gap-2 text-sm">
                  <User className="w-3.5 h-3.5 text-zinc-500 mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-400">
                    {(info.cast || item.cast).split(",").slice(0, 6).join(", ")}
                  </span>
                </div>
              )}

              {/* Season Tabs */}
              {seasons.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    {seasons.map((s) => (
                      <button
                        key={s}
                        data-testid={`season-tab-${s}`}
                        onClick={() => setSelectedSeason(s)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedSeason === s
                            ? "bg-white text-black"
                            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                        }`}
                      >
                        Sezon {s}
                      </button>
                    ))}
                  </div>

                  {/* Episodes */}
                  <div className="space-y-1">
                    {currentEpisodes.map((ep) => (
                      <EpisodeRow
                        key={ep.id || ep.episode_num}
                        episode={ep}
                        seasonNum={selectedSeason}
                        onPlay={playEpisode}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SeriesPage() {
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroSeries, setHeroSeries] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [serverBanners, setServerBanners] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await api.getSeriesContent({ per_page: 100, order_by: 'addtime_desc' });
        const items = data.content || [];
        setSeriesList(items);

        // Server banners come first in rotation
        let banners = [];
        try {
          const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/banners?location=series`);
          banners = resp.data.banners || [];
        } catch { /* fallback */ }
        setServerBanners(banners);

        if (items.length > 0) {
          setHeroSeries(items[0]);
        }
      } catch (err) {
        console.error("Failed to fetch series:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div data-testid="series-loading" className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-500">Seriallar yüklənir...</span>
        </div>
      </div>
    );
  }

  const heroBackdrop = heroSeries?.backdrop_path?.[0];

  // Mixed rotation: server banners first, then the featured series as an extra slide
  const seriesSlideImage = heroBackdrop ? proxyImageUrl(heroBackdrop) : (heroSeries?.cover ? proxyImageUrl(heroSeries.cover) : null);
  const combinedSlides = serverBanners.length > 0
    ? [
        ...serverBanners,
        ...(heroSeries && seriesSlideImage
          ? [{
              id: `content-${heroSeries.series_id}`,
              title: heroSeries.name,
              description: heroSeries.plot || "",
              image_url: seriesSlideImage,
              action: () => setSelectedSeries(heroSeries),
              action_label: "Bölümləri gör",
            }]
          : []),
      ]
    : [];

  return (
    <div data-testid="series-page" className="space-y-8">
      {/* Server Banner Hero (admin-managed) or fallback series hero */}
      {serverBanners.length > 0 ? (
        <BannerHero banners={combinedSlides} />
      ) : heroSeries && (
        <div data-testid="series-hero" className="relative h-[280px] sm:h-[360px] md:h-[420px] overflow-hidden">
          {heroBackdrop ? (
            <img src={proxyImageUrl(heroBackdrop)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : heroSeries.cover ? (
            <img src={proxyImageUrl(heroSeries.cover)} alt="" className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/30" />

          <div className="relative z-10 h-full flex flex-col justify-end p-5 sm:p-6 md:p-8 max-w-xl">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 leading-tight">{heroSeries.name}</h1>
            <div className="flex items-center gap-3 text-sm text-zinc-400 mb-2">
              {heroSeries.release_date && <span>{heroSeries.release_date.slice(0, 4)}</span>}
              {heroSeries.rating && (
                <span className="flex items-center gap-1 text-yellow-400">
                  <Star className="w-3.5 h-3.5" fill="currentColor" />
                  {parseFloat(heroSeries.rating).toFixed(1)}
                </span>
              )}
            </div>
            {heroSeries.genre && (
              <p className="text-sm text-zinc-500 mb-3">{heroSeries.genre}</p>
            )}
            {heroSeries.plot && (
              <p className="text-sm text-zinc-400 mb-5 line-clamp-2">{heroSeries.plot}</p>
            )}
            <div className="flex gap-3">
              <button
                data-testid="hero-details-btn"
                onClick={() => setSelectedSeries(heroSeries)}
                className="flex items-center gap-2 px-6 py-2.5 bg-white text-black font-semibold rounded-lg hover:bg-zinc-200 transition-colors text-sm"
              >
                <Play className="w-4 h-4" fill="currentColor" />
                Bölümləri gör
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="px-4 sm:px-6">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold text-zinc-100">Bütün Seriallar</h2>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
          {seriesList.map((item) => (
            <SeriesCard key={item.series_id} item={item} onClick={setSelectedSeries} />
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedSeries && (
        <SeriesDetailModal
          item={selectedSeries}
          onClose={() => setSelectedSeries(null)}
        />
      )}
    </div>
  );
}
